import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { changeset } from 'vega';
import vegaEmbed from 'vega-embed';
import type { ArtifactContentChartschema, MetricsIntentQueryRangeRequest, MetricsSeriesResponse } from '@app/types/sdk';

import {
  buildChartSpec,
  buildMetricsRangeRequest,
  chartPrimaryDatasetName,
  datasetNameForRef,
  defaultDataRefRep,
  metricsSeriesToRows,
  staticDatasetRowsFromSnapshots,
  type ChartDatasetMap,
  type VegaLiteSpec
} from '@app/utils/chart';

type ArtifactDocument = {
  content?: any;
  views?: Array<{ rep?: string; content?: any }>;
  presentationHints?: { defaultView?: string };
};

type Props = {
  chart: ArtifactContentChartschema;
  fetchArtifactDoc: (artifactId: string, full: boolean) => Promise<ArtifactDocument>;
  queryMetricRange?: (request: MetricsIntentQueryRangeRequest) => Promise<MetricsSeriesResponse>;
  fullTables?: boolean;
  className?: string;
};

const coerceString = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null);

const pickTableRowsFromDoc = (doc: ArtifactDocument, rep?: string): Record<string, unknown>[] => {
  const anyDoc: any = doc;
  const selectedRep =
    coerceString(rep) ??
    coerceString(anyDoc?.presentationHints?.defaultView) ??
    (Array.isArray(anyDoc?.views) && anyDoc.views.length > 0 ? coerceString(anyDoc.views[0]?.rep) : null);
  const view = selectedRep && Array.isArray(anyDoc?.views) ? anyDoc.views.find((v: any) => v && v.rep === selectedRep) : null;
  const content = (view?.content ?? anyDoc?.content) as any;
  if (content?.type !== 'table') {
    return [];
  }
  return Array.isArray(content.rows) ? (content.rows as Record<string, unknown>[]) : [];
};

const hasMetricsBinding = (chart: ArtifactContentChartschema): boolean => {
  const binding = chart.dataBinding as any;
  return binding?.source === 'metrics' && binding?.metrics != null;
};

const shouldPollChart = (chart: ArtifactContentChartschema): boolean => {
  if (!hasMetricsBinding(chart)) {
    return false;
  }
  const mode = String((chart.refreshPlan as any)?.mode ?? '').trim();
  return mode === 'poll';
};

const preferredSeriesLabels = (chart: ArtifactContentChartschema): string[] => {
  const metrics = (chart.dataBinding as any)?.metrics;
  const explicit = Array.isArray(metrics?.seriesLabelFields) ? metrics.seriesLabelFields : [];
  if (explicit.length > 0) {
    return explicit.filter((v: unknown): v is string => typeof v === 'string' && v.trim().length > 0);
  }
  const queryPlanLabels = Array.isArray(metrics?.queryPlan?.seriesLabelFields) ? metrics.queryPlan.seriesLabelFields : [];
  return queryPlanLabels.filter((v: unknown): v is string => typeof v === 'string' && v.trim().length > 0);
};

const formatLastUpdated = (date: Date | null): string => (date ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : 'Never');

export const ChartArtifactView = ({ chart, fetchArtifactDoc, queryMetricRange, fullTables, className }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<any>(null);
  const mountedRef = useRef(false);
  const consecutiveFailuresRef = useRef(0);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [spec, setSpec] = useState<VegaLiteSpec | null>(null);
  const [viewReady, setViewReady] = useState(false);
  const [paused, setPaused] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);

  const dataRefs = useMemo(() => (Array.isArray(chart.dataRefs) ? chart.dataRefs : []), [chart.dataRefs]);
  const dynamic = hasMetricsBinding(chart);
  const polling = shouldPollChart(chart);
  const datasetName = useMemo(() => chartPrimaryDatasetName(chart), [chart]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    setWarnings([]);
    setSpec(null);
    setViewReady(false);
    setPollError(null);
    consecutiveFailuresRef.current = 0;

    (async () => {
      try {
        const datasets: ChartDatasetMap = staticDatasetRowsFromSnapshots(chart);
        for (let i = 0; i < dataRefs.length; i += 1) {
          const ref = dataRefs[i] as any;
          const artifactId = coerceString(ref.artifactId);
          if (!artifactId) {
            throw new Error(`chart.dataRefs[${i}].artifactId missing`);
          }
          const tableDoc = await fetchArtifactDoc(artifactId, Boolean(fullTables));
          const rows = pickTableRowsFromDoc(tableDoc, defaultDataRefRep(ref));
          const refDatasetName = datasetNameForRef(ref, i, chart.spec as VegaLiteSpec);
          datasets[refDatasetName] = rows;
        }

        if (dynamic && !datasets[datasetName]) {
          datasets[datasetName] = [];
        }
        if (!dynamic && dataRefs.length === 0 && Object.keys(datasets).length === 0) {
          throw new Error('chart must include dataRefs or lastSnapshot rows');
        }

        const built = buildChartSpec(chart, datasets);
        if (!built.ok) {
          throw new Error(built.error);
        }
        if (cancelled) return;
        setWarnings(built.warnings);
        setSpec(built.spec);
        setStatus('ready');
        if (Object.keys(datasets).length > 0) {
          setLastUpdated(new Date());
        }
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to render chart');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chart, dataRefs, datasetName, dynamic, fetchArtifactDoc, fullTables]);

  useEffect(() => {
    if (status !== 'ready' || !spec || !containerRef.current) {
      return;
    }
    let cancelled = false;
    const el = containerRef.current;
    el.innerHTML = '';
    setViewReady(false);
    (async () => {
      try {
        const result = await vegaEmbed(el, spec as any, {
          actions: false,
          renderer: 'canvas'
        });
        if (cancelled) {
          try {
            result?.view?.finalize?.();
          } catch {
            // ignore
          }
          return;
        }
        viewRef.current = result?.view ?? null;
        setViewReady(Boolean(result?.view));
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Failed to render Vega-Lite chart');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      setViewReady(false);
      try {
        viewRef.current?.finalize?.();
      } catch {
        // ignore
      }
      viewRef.current = null;
    };
  }, [spec, status]);

  const applyDatasetRows = useCallback(
    async (rows: Record<string, unknown>[]) => {
      const view = viewRef.current;
      if (!view) {
        return;
      }
      const change = changeset().remove(() => true).insert(rows);
      await view.change(datasetName, change).runAsync();
    },
    [datasetName]
  );

  useEffect(() => {
    if (!polling || !queryMetricRange || paused || status !== 'ready' || !viewReady) {
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    const plan = chart.refreshPlan as any;
    const intervalSeconds = Math.max(5, Number(plan?.intervalSeconds) || 30);
    const visibleOnly = plan?.visibleOnly !== false;
    const maxFailures = Math.max(1, Number(plan?.maxConsecutiveFailures) || 3);
    const labels = preferredSeriesLabels(chart);

    const schedule = () => {
      timer = window.setTimeout(poll, intervalSeconds * 1000);
    };

    const poll = async () => {
      if (cancelled || !mountedRef.current) {
        return;
      }
      if (visibleOnly && typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        schedule();
        return;
      }
      setRefreshing(true);
      try {
        const request = buildMetricsRangeRequest(chart, new Date());
        const response = await queryMetricRange(request);
        const rows = metricsSeriesToRows(response, labels);
        await applyDatasetRows(rows);
        if (cancelled || !mountedRef.current) {
          return;
        }
        consecutiveFailuresRef.current = 0;
        setPollError(null);
        setLastUpdated(new Date());
      } catch (e) {
        if (cancelled || !mountedRef.current) {
          return;
        }
        consecutiveFailuresRef.current += 1;
        const message = e instanceof Error ? e.message : 'Failed to refresh chart data';
        setPollError(consecutiveFailuresRef.current >= maxFailures ? message : null);
      } finally {
        if (!cancelled && mountedRef.current) {
          setRefreshing(false);
          schedule();
        }
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer != null) {
        window.clearTimeout(timer);
      }
    };
  }, [applyDatasetRows, chart, paused, polling, queryMetricRange, status, viewReady]);

  if (status === 'loading') {
    return (
      <div className={className}>
        <p>Rendering chart...</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={className}>
        <p className="chat-timeline__artifact-error">{error ?? 'Failed to render chart.'}</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {warnings.length > 0 && (
        <details className="chat-artifact-chart__warnings">
          <summary>Chart warnings</summary>
          <ul>
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </details>
      )}
      <div ref={containerRef} className="chat-artifact-chart__canvas" />
      {dynamic && (
        <div className="chat-artifact-chart__status">
          <span>{refreshing ? 'Refreshing...' : `Last updated ${formatLastUpdated(lastUpdated)}`}</span>
          {pollError && <span className="chat-artifact-chart__error">{pollError}</span>}
          {polling && (
            <button type="button" className="chat-artifact-chart__pause" onClick={() => setPaused((prev) => !prev)}>
              {paused ? 'Resume' : 'Pause'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
